import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import Table from './styled/Table';
import Cell from './components/Cell';
import { RowEditable, EditableContext } from './components/CellEditable';
import { makeColumns, makeDataSource } from './util';

const components = {
  body: {
    row: RowEditable,
    cell: Cell,
  },
};

const hasEditableColumns = columns =>
  columns.some(({ editable, children }) => {
    if (editable) return true;
    if (!isEmpty(children)) return hasEditableColumns(children);
    return false;
  });

const getActiveEditableRecord = dataSource => {
  let recordKey = null;
  dataSource.some(record => {
    if (record.activeEdit) {
      recordKey = record.key;
    } else if (!isEmpty(record.children)) {
      const child = record.children.find(({ activeEdit }) => activeEdit);
      if (child) recordKey = child.key;
    }
    return recordKey;
  });
  return recordKey;
};

export default class WorkOperationsTable extends PureComponent {
  state = {
    editingKey: null,
    editingMode: hasEditableColumns(this.props.columns),
  };

  componentDidMount() {
    this.setActiveEditKey();
    document.addEventListener('keydown', this.escapeEditingRow, false);
  }

  componentDidUpdate() {
    this.setActiveEditKey();
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.escapeEditingRow, false);
  }

  setEditingKey = editingKey => {
    if (!this.state.editingMode || editingKey === this.state.editingKey) return;
    if (editingKey) {
      const record = this.getRecordByKey(editingKey);
      if (!record || record.editable === false) return;
    }
    const prevEditingKey = this.state.editingKey;
    this.setState({ editingKey });
    this.props.onSetEditRow(editingKey, prevEditingKey);
  };

  setActiveEditKey = () => {
    const { dataSource } = this.props;
    const activeEditKey = getActiveEditableRecord(dataSource);
    if (activeEditKey) this.setEditingKey(activeEditKey);
  };

  getRecordByKey = recordKey => {
    let record = null;
    this.dataSource.some(data => {
      if (data.key === recordKey) {
        record = data;
      } else if (!isEmpty(data.children)) {
        record = data.children.find(({ key }) => key === recordKey);
      }
      return record;
    });
    return record;
  };

  get columns() {
    const { columns, validator, triggerEditOnCellClick } = this.props;
    const { editingKey } = this.state;
    return makeColumns(columns, {
      editingKey,
      validator,
      onCellClick: key => triggerEditOnCellClick && this.setEditingKey(key),
    });
  }

  get dataSource() {
    const { dataSource } = this.props;
    return makeDataSource(dataSource, this.columns);
  }

  escapeEditingRow = ({ keyCode }) => (keyCode === 27 ? this.setEditingKey(null) : null);

  render() {
    const { columns, dataSource, className, ...attrs } = this.props;
    return (
      <Table
        className={classNames(className, 'work-operations-table')}
        columns={this.columns}
        dataSource={this.dataSource}
        components={components}
        pagination={false}
        size="middle"
        onRow={record => ({ record })}
        {...attrs}
      />
    );
  }
}

WorkOperationsTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
    })
  ),
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  validator: PropTypes.func,
  triggerEditOnCellClick: PropTypes.bool,
  parentStyledImportant: PropTypes.bool,
  onSetEditRow: PropTypes.func,
  className: PropTypes.any,
};

WorkOperationsTable.defaultProps = {
  columns: [],
  dataSource: [],
  triggerEditOnCellClick: true,
  parentStyledImportant: false,
  validator: () => {},
  onSetEditRow: () => {},
  className: null,
};

export { EditableContext };
